import Filter from '@/src/components/organism/Filter';
import { useEffect, useRef, useState } from 'react';
import { recyclingCouponFilters } from './filter';
import { useQuery } from '@tanstack/react-query';
import useQueryValues from '@/src/hooks/useQueryValues';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { AgGridReact } from 'ag-grid-react';
import Table from '@/src/components/atom/Table';
import { recyclingCouponListColumn } from './column';
import SectionCardNoneTitle from '@/src/components/molecule/SectionCardNoneTitle';
import Typo from '@/src/components/atom/Typo';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import usePaginationQuery from '@/src/hooks/usePaginationQuery';
import { NAV_CONST } from '@/src/constants/nav';
import {
  getRecyclingCouponByIdAPI,
  getRecyclingCouponExcelAPI,
  getRecyclingCouponsAPI,
} from '@/src/apis/rest/recyclingCoupon';
import RecyclingCouponDetailModal from '@/src/pages/RecyclingCoupon/components/RecyclingCouponDetailModal';
import RecyclingCouponEditlModal from '@/src/pages/RecyclingCoupon/components/RecyclingCouponEditModal';
import RecyclingCouponCreateModal from '@/src/pages/RecyclingCoupon/components/RecyclingCouponCreateModal';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';

type ModalState = 'NONE' | 'CREATE' | 'EDIT' | 'DETAIL';

export default function RecyclingCouponListPage() {
  const { defaultValues } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
    postedAt: ['onAt', 'offAt'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();

  const gridRef = useRef<AgGridReact>(null);
  const [values, setValues] = useState(defaultValues);
  const [modalState, setModalState] = useState<ModalState>('NONE');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // query
  const {
    data: RecyclingCouponListOrigin,
    isError,
    isFetching,
    refetch: refetchRecyclingCouponList,
  } = useQuery({
    queryKey: ['recycling-coupon', values, page, pageSize],
    queryFn: () => {
      return getRecyclingCouponsAPI({
        ...values,
        page,
        pageSize,
      });
    },
  });
  const {
    data: recyclingCouponDetailData,
    refetch: refetchRecyclingCouponDetail,
  } = useQuery({
    queryKey: ['recycling-coupon', selectedId],
    queryFn: () => {
      return getRecyclingCouponByIdAPI({
        id: Number(selectedId),
      });
    },
    enabled: !!selectedId,
  });

  // event
  const handleDetailClick = (id: number) => {
    setSelectedId(id);
    handleOpenModal('DETAIL');
  };
  const handleOpenModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseModal = () => {
    setModalState('NONE');
  };
  const handleToggleModal = (type: ModalState) => {
    if (modalState === type) {
      handleCloseModal();
    } else {
      setModalState(type);
    }
  };

  // effect
  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.recyclingCouponManagement}
        breadcrumbList={[NAV_CONST.recyclingCouponManagement]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={recyclingCouponFilters()}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setValues(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">배송비 쿠폰 목록</Typo>
              <div className="flex gap-2">
                <ExcelDownloadButton
                  disabled={
                    isError ||
                    isFetching ||
                    !RecyclingCouponListOrigin?.rows?.length
                  }
                  fileName="배송비_쿠폰_리스트"
                  downloadExcelAPI={getRecyclingCouponExcelAPI}
                  searchParams={values}
                />
                <Button
                  buttonSize="medium"
                  onClick={() => {
                    handleOpenModal('CREATE');
                  }}
                >
                  <div className="flex items-center gap-x-2">
                    <LucideIcon name="Plus" color="white" />
                    배송비 쿠폰 등록
                  </div>
                </Button>
              </div>
            </div>

            <Table
              ref={gridRef}
              rowData={isError ? [] : RecyclingCouponListOrigin?.rows || []}
              isPaginationDataMapping
              columnDefs={recyclingCouponListColumn(e => {
                handleDetailClick(e);
              })}
              total={RecyclingCouponListOrigin?.count || 0}
              page={page}
              pageSize={pageSize}
              handlePaginationClick={handlePageChange}
            />
          </SectionCardNoneTitle>
        </div>
      </div>

      {/* 모달 */}
      {recyclingCouponDetailData && modalState === 'DETAIL' && (
        <RecyclingCouponDetailModal
          open={modalState === 'DETAIL'}
          onOpenChange={e => {
            if (!e) setSelectedId(null);
            handleToggleModal('DETAIL');
          }}
          data={recyclingCouponDetailData}
          onOkClick={() => handleOpenModal('EDIT')}
        />
      )}
      {recyclingCouponDetailData && modalState === 'EDIT' && (
        <RecyclingCouponEditlModal
          open={modalState === 'EDIT'}
          onOpenChange={handleCloseModal}
          onOk={() => {
            refetchRecyclingCouponList();
            refetchRecyclingCouponDetail();
            setModalState('DETAIL');
          }}
          data={recyclingCouponDetailData}
        />
      )}
      {modalState === 'CREATE' && (
        <RecyclingCouponCreateModal
          open={modalState === 'CREATE'}
          onOpenChange={handleCloseModal}
          onOk={id => {
            refetchRecyclingCouponList();
            refetchRecyclingCouponDetail();
            setSelectedId(id);
            setModalState('DETAIL');
          }}
        />
      )}
    </>
  );
}
